// CFG-IA.B1 (#1541) — wizard step: Storage Provider (D2=embed StorageProviderCard).
// Replaces the storage section of the old Step1Infrastructure.
import { useState } from 'react';
import { apiFetch } from '../../../lib/api';
import StorageProviderCard, { type StorageProvider } from '../../settings/StorageProviderCard';

export interface StorageProviderData {
  storageProvider: StorageProvider;
}

interface Props {
  tenantId:       string;
  onStepComplete: (data: StorageProviderData) => void;
  savedData?:     StorageProviderData;
}

export default function StorageProviderStep({ tenantId, onStepComplete, savedData }: Props) {
  const [selected, setSelected] = useState<StorageProvider>(savedData?.storageProvider ?? 'GOOGLE_DRIVE');
  const [saving,   setSaving]   = useState(false);

  const handleContinue = async () => {
    setSaving(true);
    try {
      // DRV-1 (#2879, D14) — SharePoint is not available yet and the API
      // rejects it with 400. A restored wizard state can still hold it, so
      // it is never sent: the POST is skipped and the tenant's stored
      // provider is left as it is (the endpoint is preserve-on-absence).
      if (selected !== 'SHAREPOINT') {
        await apiFetch(`/tenants/${tenantId}/infrastructure-config`, {
          method: 'POST',
          body:   JSON.stringify({ storageProvider: selected }),
        });
      }
      onStepComplete({ storageProvider: selected });
    } catch {
      onStepComplete({ storageProvider: selected });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: '24px 0' }}>
      <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
        Storage Provider
      </h2>
      <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24 }}>
        Choose where BluePrint stores project artefacts and shared documents. You can change this later in Settings.
      </p>

      <StorageProviderCard
        tenantId={tenantId}
        onSaved={setSelected}
        showIntro={false}
      />

      <button
        onClick={() => void handleContinue()}
        disabled={saving}
        data-testid="storage-continue-btn"
        style={{
          fontFamily:   'var(--font-body)',
          fontSize:     14,
          fontWeight:   600,
          padding:      '10px 24px',
          background:   'var(--primary)',
          color:        '#fff',
          border:       'none',
          borderRadius: 'var(--radius-sm)',
          cursor:       saving ? 'not-allowed' : 'pointer',
          opacity:      saving ? 0.6 : 1,
          marginTop:    16,
        }}
      >
        {saving ? 'Saving...' : 'Continue'}
      </button>
    </div>
  );
}
